import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Tag } from '@carbon/react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { PageHeader, ErrorState, BlockSkeleton, EmptyState } from '../components/states';
import { Money, When, compactVnd } from '../components/format';
import { ExportButton, FilterBar } from '../components/Filters';
import {
  GRAIN_LABEL, GrainSwitch, Hours, LineChart, Num, PARTIAL_NOTE, PERIOD_WORD, Panel, Rate, grainFilter, periodCell, periodTick, scopeFields, useReportFilters,
  type Grain,
} from '../components/Analytics';

// Screen 8 of docs/cms-golive/kpi-catalogue.md: the agent tree ranked by subtree GGR with members,
// new, FTD, active, turnover, GGR, commission, commission % of GGR, agent NGR, self-play share,
// payouts and carry-over; drill-down agent -> sub-agents -> members (P1-D17). The network and one
// agent's subtree also day by day, week by week or month by month.

type Num_ = { members: number; newMembers: number; ftd: number; active: number; turnover: number; ggr: number; bonus: number; commissionOnMembers: number; ngr: number };
type AgentRow = {
  id: string; code: string; name: string; parentId: string | null; depth: number; status: string; rateBps: number; children: number;
  direct: Num_; subtree: Num_;
  commission: { accrued: number; reversed: number; net: number; vested: number; paid: number; selfPlay: number; selfPlayShare: number | null; pctGgr: number | null; pctTurnover: number | null };
  balance: { balance: number; carryOver: number };
  payoutSla: { paid: number; p50Hours: number | null; p95Hours: number | null };
};
type Period = {
  day: string; partial: boolean; members: number; newMembers: number; ftd: number; active: number; turnover: number; ggr: number; bonus: number; commissionOnMembers: number; ngr: number;
  accrued: number; reversed: number; net: number; vested: number; paid: number; payouts: number;
};
type Report = { from: string; to: string; rows: AgentRow[]; payoutSla: { paid: number; p50Hours: number | null; p95Hours: number | null }; grain: Grain; trend: Period[] };
type Drill = {
  agent: AgentRow; children: AgentRow[]; grain: Grain; trend: Period[];
  members: { userId: number; nick: string | null; joinedAt: string; ftdAt: string | null; turnover: number; ggr: number; bonus: number; commission: number; ngr: number; lastPlay: string | null }[];
};

const HEAD = (
  <thead><tr>
    <th>Đại lý</th><th className="r">Thành viên</th><th className="r">Mới</th><th className="r">Nạp đầu</th><th className="r">Đang chơi</th>
    <th className="r">Doanh số</th><th className="r">GGR</th><th className="r">NGR</th><th className="r">Hoa hồng ròng</th><th className="r">HH / GGR</th>
    <th className="r">Tự chơi</th><th className="r">Đã chi</th><th className="r">Nợ chuyển kỳ</th>
  </tr></thead>
);

function Cells({ r }: { r: AgentRow }) {
  return <>
    <td className="r"><Num v={r.subtree.members} /></td><td className="r"><Num v={r.subtree.newMembers} /></td><td className="r"><Num v={r.subtree.ftd} /></td><td className="r"><Num v={r.subtree.active} /></td>
    <td className="r"><Money v={r.subtree.turnover} /></td><td className="r"><Money v={r.subtree.ggr} signed /></td><td className="r"><Money v={r.subtree.ngr} signed /></td>
    <td className="r" title={`nhận ${r.commission.accrued}, bị đảo ${r.commission.reversed}`}><Money v={r.commission.net} /></td><td className="r"><Rate v={r.commission.pctGgr} /></td>
    <td className="r"><Rate v={r.commission.selfPlayShare} /></td><td className="r"><Money v={r.commission.paid} /></td><td className="r"><Money v={r.balance.carryOver} /></td>
  </>;
}

function Name({ r, link = true }: { r: AgentRow; link?: boolean }) {
  return (
    <td className={`is-title indent-${Math.min(3, r.depth)}`}>
      {link ? <Link className="cell-link" to={`/stats/agents/${r.id}`}>{r.code}</Link> : r.code} <span className="muted">{r.name}</span>
      {r.status !== 'ACTIVE' ? <> <Tag type="gray" size="sm">{r.status}</Tag></> : null}
    </td>
  );
}

/** The agents' figures of each day, week or month of the range: the whole network or one agent's subtree. */
function AgentPeriods({ g, trend, who, path, query }: { g: Grain; trend: Period[]; who: string; path: string; query: Record<string, string> }) {
  const word = PERIOD_WORD[g];
  const rows = [...trend].reverse();
  const ticks = trend.map((r) => periodTick(r.day, g, r.partial));
  const head = <th>{GRAIN_LABEL[g]}</th>;
  const cell = (r: Period) => <td className="num is-title">{periodCell(r.day, g, r.partial)}</td>;
  const partial = g !== 'day' ? ' ' + PARTIAL_NOTE : '';
  return (
    <>
      <Panel title={`${who} theo ${word}`} aside={<ExportButton path={path} query={{ ...query, section: 'periods' }} label={`Xuất CSV theo ${word}`} />}>
        <div className="split split--even">
          <LineChart title={`GGR, NGR và hoa hồng trên thành viên theo ${word}`} labels={ticks} height={180} format={(v) => compactVnd(Math.round(v))}
            series={[{ label: 'GGR', points: trend.map((r) => r.ggr) }, { label: 'NGR', points: trend.map((r) => r.ngr) }, { label: 'Hoa hồng', points: trend.map((r) => r.commissionOnMembers) }]} />
          <LineChart title={`Thành viên mới, nạp đầu và đang chơi theo ${word}`} labels={ticks} height={180} format={(v) => String(Math.round(v))}
            series={[{ label: 'Mới', points: trend.map((r) => r.newMembers) }, { label: 'Nạp đầu', points: trend.map((r) => r.ftd) }, { label: 'Đang chơi', points: trend.map((r) => r.active) }]} />
        </div>
        <div className="table-scroll">
          <table className="plain">
            <thead><tr>{head}<th className="r">Thành viên cuối {word}</th><th className="r">Mới</th><th className="r">Nạp đầu</th><th className="r">Đang chơi</th><th className="r">Doanh số</th>
              <th className="r">GGR</th><th className="r">Thưởng</th><th className="r">Hoa hồng trên thành viên</th><th className="r">NGR</th></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.day}>{cell(r)}
                <td className="r"><Num v={r.members} /></td><td className="r"><Num v={r.newMembers} /></td><td className="r"><Num v={r.ftd} /></td><td className="r"><Num v={r.active} /></td>
                <td className="r"><Money v={r.turnover} /></td><td className="r"><Money v={r.ggr} signed /></td><td className="r"><Money v={r.bonus} /></td>
                <td className="r"><Money v={r.commissionOnMembers} /></td><td className="r"><Money v={r.ngr} signed /></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <p className="muted">Thành viên thật (không tính tài khoản thử, bot) đã đăng ký đến ngày cuối {word}. Đang chơi đếm mỗi thành viên một lần trong mỗi {word}. Doanh số = cược - hoàn; GGR = cược - trả thưởng - hoàn; NGR = GGR - thưởng - hoa hồng các đại lý nhận trên thành viên. Người chơi của nhà cái (mã 8888) không phải thành viên đại lý.{partial}</p>
      </Panel>
      <Panel title={`Hoa hồng đại lý theo ${word}`}>
        <div className="table-scroll">
          <table className="plain">
            <thead><tr>{head}<th className="r">Hoa hồng nhận</th><th className="r">Bị đảo</th><th className="r">Hoa hồng ròng</th><th className="r">Đã mở khóa</th><th className="r">Đã chi</th><th className="r">Lần chi</th></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.day}>{cell(r)}
                <td className="r"><Money v={r.accrued} /></td><td className="r"><Money v={r.reversed} /></td><td className="r"><Money v={r.net} signed /></td>
                <td className="r"><Money v={r.vested} /></td><td className="r"><Money v={r.paid} /></td><td className="r"><Num v={r.payouts} /></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <p className="muted">Hoa hồng nhận ghi vào ngày cược; bị đảo ghi vào ngày hoàn cược, hoặc ngày ra quyết định gian lận thu hồi hoa hồng. Đã mở khóa theo ngày mở khóa, đã chi theo ngày trả tiền. Phần chênh của nhà cái (mã 8888) không tính.{partial}</p>
      </Panel>
    </>
  );
}

export function StatsAgentsPage() {
  const f = useReportFilters(30, ['grain']);
  const [grain, pickGrain] = grainFilter(f);
  const q = useQuery({ queryKey: ['analytics', 'agents', f.query], queryFn: () => api.get<Report>('/api/admin/analytics/agents', f.query) });
  const d = q.data;
  return (
    <div className="page">
      <PageHeader title="Xếp hạng đại lý" lead="Cây đại lý xếp theo GGR của cả tuyến dưới trong khoảng ngày. Số của một đại lý gồm mọi thành viên trong nhánh." />
      <GrainSwitch value={grain} onChange={pickGrain} />
      <FilterBar id="ag" fields={scopeFields(undefined, { scope: false })} filters={f} exportPath="/api/admin/analytics/agents" />
      {q.isLoading ? <BlockSkeleton lines={10} /> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : !d?.rows.length ? (
        <EmptyState title="Chưa có đại lý nào" />
      ) : (
        <>
          <AgentPeriods g={d.grain} trend={d.trend} who="Mạng lưới đại lý" path="/api/admin/analytics/agents" query={f.query} />
          <Panel title="Xếp hạng trong khoảng ngày">
            <div className="table-scroll"><table className="plain">{HEAD}<tbody>{d.rows.map((r) => <tr key={r.id}><Name r={r} /><Cells r={r} /></tr>)}</tbody></table></div>
            <p className="muted">
              Thời gian chi hoa hồng (yêu cầu đến lúc trả) trong khoảng: {d.payoutSla.paid} lần, trung vị <Hours v={d.payoutSla.p50Hours} />, p95 <Hours v={d.payoutSla.p95Hours} />.
              NGR đại lý = GGR - thưởng - hoa hồng trả cho các đại lý trên thành viên. Tự chơi = phần hoa hồng đại lý nhận từ cược của chính tài khoản chơi gắn với mình.
              Hoa hồng phần chênh của nhà cái (mã 8888) không tính là chi phí.
            </p>
          </Panel>
        </>
      )}
    </div>
  );
}

export function StatsAgentPage() {
  const { id } = useParams();
  const { can } = useAuth();
  const f = useReportFilters(30, ['grain']);
  const [grain, pickGrain] = grainFilter(f);
  const q = useQuery({ queryKey: ['analytics', 'agent', id, f.query], queryFn: () => api.get<Drill>(`/api/admin/analytics/agents/${id}`, f.query) });
  const d = q.data;
  return (
    <div className="page">
      <PageHeader title={d ? `Đại lý ${d.agent.code}` : 'Đại lý'} lead={d ? `${d.agent.name}, hoa hồng ${(d.agent.rateBps / 100).toLocaleString('vi-VN')}%` : undefined}
        actions={<><Link className="cell-link" to="/stats/agents">Về bảng xếp hạng</Link>{d ? <Link className="cell-link" to={`/agents/${d.agent.id}`}>Mở hồ sơ đại lý</Link> : null}</>} />
      <GrainSwitch value={grain} onChange={pickGrain} />
      <FilterBar id="agd" fields={scopeFields(undefined, { scope: false })} filters={f} exportPath={`/api/admin/analytics/agents/${id}`} />
      {q.isLoading ? <BlockSkeleton lines={10} /> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : !d ? null : (
        <>
          <AgentPeriods g={d.grain} trend={d.trend} who={`Nhánh ${d.agent.code}`} path={`/api/admin/analytics/agents/${id}`} query={f.query} />
          <Panel title="Tổng nhánh và tuyến dưới trực tiếp">
            <div className="table-scroll"><table className="plain">{HEAD}<tbody>
              <tr><Name r={{ ...d.agent, depth: 0 }} link={false} /><Cells r={d.agent} /></tr>
              {d.children.map((r) => <tr key={r.id}><Name r={{ ...r, depth: 1 }} /><Cells r={r} /></tr>)}
            </tbody></table></div>
            <p className="muted">Hoa hồng nhận {d.agent.commission.accrued.toLocaleString('vi-VN')}, bị đảo {d.agent.commission.reversed.toLocaleString('vi-VN')}, đã mở khóa {d.agent.commission.vested.toLocaleString('vi-VN')}; số dư ví đại lý <Money v={d.agent.balance.balance} unit />.</p>
          </Panel>
          <Panel title={`Thành viên trực tiếp (${d.members.length})`}>
            {d.members.length ? (
              <div className="table-scroll"><table className="plain">
                <thead><tr><th>Người chơi</th><th>Vào lúc</th><th>Nạp đầu</th><th className="r">Doanh số</th><th className="r">GGR</th><th className="r">Thưởng</th><th className="r">Hoa hồng</th><th className="r">NGR</th><th>Chơi lần cuối</th></tr></thead>
                <tbody>{d.members.map((m) => (
                  <tr key={m.userId}>
                    <td className="is-title">{can('users.view') ? <Link className="cell-link" to={`/stats/player/${m.userId}`}>{m.nick ?? '#' + m.userId}</Link> : m.nick ?? '#' + m.userId}</td>
                    <td><When v={m.joinedAt} /></td><td>{m.ftdAt ? <When v={m.ftdAt} /> : <span className="muted">-</span>}</td>
                    <td className="r"><Money v={m.turnover} /></td><td className="r"><Money v={m.ggr} signed /></td><td className="r"><Money v={m.bonus} /></td>
                    <td className="r"><Money v={m.commission} /></td><td className="r"><Money v={m.ngr} signed /></td><td className="num">{m.lastPlay ?? '-'}</td>
                  </tr>
                ))}</tbody>
              </table></div>
            ) : <p className="muted">Đại lý này chưa có thành viên trực tiếp.</p>}
          </Panel>
        </>
      )}
    </div>
  );
}
